import { useEffect, useRef, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Phone, ShieldCheck, LockKey, ArrowLeft, Buildings, ClipboardText, UserCircle, Wrench, CheckCircle, ChatCircleDots, ShieldStar, Megaphone, Sparkle, UsersThree, CalendarCheck, Wallet, Lightning } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

// Words that rotate inside the hero headline (adds life to the web login).
const HERO_WORDS = ["sorted.", "simpler.", "in sync.", "on time."];

// Floating feature chips on the brand panel.
const HERO_FEATURES = [
  { icon: UsersThree, label: "Visitors & gate" },
  { icon: Wrench, label: "Complaints" },
  { icon: CalendarCheck, label: "Amenity booking" },
  { icon: Wallet, label: "Society finance" },
  { icon: ShieldStar, label: "Staff & checklists" },
  { icon: Megaphone, label: "Announcements" },
];

const WINGS = [
  { code: "C", name: "C Wing", floors: 19, flatsPerFloor: 8 },
  { code: "D", name: "D Wing", floors: 12, flatsPerFloor: 4 },
];

const STAFF_ROLES = [
  { code: "MANAGER", label: "Society Manager", sub: "Tasks & complaints", accent: "lime" },
  { code: "SECURITY", label: "Security", sub: "Directory, attendance & checklist", accent: "cyan" },
  { code: "CHAIRPERSON", label: "Chairperson", sub: "Society oversight", accent: "cyan" },
  { code: "ADMIN", label: "Admin", sub: "System administration", accent: "pink" },
];

// Looping task-management animation: a ticket flows horizontally through its lifecycle.
const FLOW = [
  { label: "Raised", icon: ClipboardText },
  { label: "Assigned", icon: UserCircle },
  { label: "Working", icon: Wrench },
  { label: "Resolved", icon: CheckCircle },
];

function TaskFlowAnimation() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s >= FLOW.length ? 0 : s + 1)), 900);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="mt-2 flex items-start">
      {FLOW.map((s, i) => {
        const done = i < step;
        const active = i === step - 1;
        const Icon = s.icon;
        return (
          <Fragment key={s.label}>
            <div className="flex w-12 shrink-0 flex-col items-center gap-1">
              <motion.span
                className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${done ? "border-neon-cyan bg-neon-cyan text-black" : "border-white/20 text-white/40"}`}
                animate={{ scale: active ? [1, 1.2, 1] : 1 }} transition={{ duration: 0.4 }}>
                <Icon size={16} weight={done ? "fill" : "regular"} />
              </motion.span>
              <span className={`text-center text-[9px] font-bold uppercase tracking-wide ${done ? "text-white/85" : "text-white/40"}`}>{s.label}</span>
            </div>
            {i < FLOW.length - 1 && (
              <div className="mt-4 h-0.5 flex-1 overflow-hidden rounded-full bg-white/10">
                <motion.div className="h-full rounded-full bg-neon-cyan" animate={{ width: i < step - 1 ? "100%" : "0%" }} transition={{ duration: 0.4 }} />
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

// A premium, auto-rotating value-prop showcase that fills the lower half of the
// login screen with ambient motion instead of dead space.
const SHOWCASE = [
  { icon: Wrench, title: "Raise & track tickets", sub: "Plumbing to lifts — sorted in a tap." },
  { icon: ChatCircleDots, title: "Talk to the office", sub: "Chat directly. No more phone tag." },
  { icon: ShieldStar, title: "Staff & daily checks", sub: "Attendance and checklists, on time." },
  { icon: Megaphone, title: "Never miss a notice", sub: "Announcements land right here." },
];

function LoginShowcase() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % SHOWCASE.length), 2600);
    return () => clearInterval(id);
  }, []);
  const Icon = SHOWCASE[i].icon;
  return (
    <div className="relative flex w-full flex-col items-center justify-center overflow-hidden">
      {/* ambient orbs */}
      <motion.div className="pointer-events-none absolute -z-10 h-52 w-52 rounded-full bg-neon-cyan/10 blur-3xl"
        animate={{ scale: [1, 1.18, 1], opacity: [0.45, 0.75, 0.45] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="pointer-events-none absolute -z-10 -bottom-6 h-40 w-40 rounded-full bg-neon-lime/10 blur-3xl"
        animate={{ scale: [1.1, 1, 1.1], opacity: [0.4, 0.65, 0.4] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />

      {/* icon with expanding pulse rings */}
      <div className="relative flex h-20 w-20 items-center justify-center">
        {[0, 1].map((d) => (
          <motion.span key={d} className="absolute inset-0 rounded-full border border-neon-cyan/40"
            animate={{ scale: [1, 1.6], opacity: [0.55, 0] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut", delay: d * 1.1 }} />
        ))}
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-neon-cyan to-neon-lime text-black shadow-glow-cyan">
          <AnimatePresence mode="wait">
            <motion.span key={i} initial={{ scale: 0.4, opacity: 0, rotate: -25 }} animate={{ scale: 1, opacity: 1, rotate: 0 }} exit={{ scale: 0.4, opacity: 0 }} transition={{ duration: 0.35 }}>
              <Icon size={30} weight="fill" />
            </motion.span>
          </AnimatePresence>
        </div>
      </div>

      {/* rotating headline */}
      <div className="mt-5 flex h-14 items-start justify-center text-center">
        <AnimatePresence mode="wait">
          <motion.div key={i} initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -12, opacity: 0 }} transition={{ duration: 0.35 }}>
            <p className="font-display text-base font-extrabold text-white">{SHOWCASE[i].title}</p>
            <p className="mt-1 text-[12px] text-white/55">{SHOWCASE[i].sub}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* progress dots */}
      <div className="mt-3 flex items-center gap-1.5">
        {SHOWCASE.map((_, j) => (
          <span key={j} className={`h-1.5 rounded-full transition-all duration-300 ${j === i ? "w-5 bg-neon-cyan" : "w-1.5 bg-white/20"}`} />
        ))}
      </div>
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [tab, setTab] = useState("resident"); // resident | society
  const [step, setStep] = useState("phone"); // phone | otp | onboard
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [pendingToken, setPendingToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [cooldown, setCooldown] = useState(0);

  // Onboard fields
  const [obName, setObName] = useState("");
  const [obWing, setObWing] = useState("");
  const [obFloor, setObFloor] = useState("");
  const [obFlat, setObFlat] = useState("");

  // Staff
  const [staffCode, setStaffCode] = useState("");
  const [staffPwd, setStaffPwd] = useState("");

  // Hero interactivity (web): rotating headline word + pointer-follow glow.
  const [wordIdx, setWordIdx] = useState(0);
  const heroRef = useRef(null);
  useEffect(() => {
    const id = setInterval(() => setWordIdx((w) => (w + 1) % HERO_WORDS.length), 2200);
    return () => clearInterval(id);
  }, []);
  function onHeroMove(e) {
    const el = heroRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
    el.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
  }

  const otpRefs = [useRef(), useRef(), useRef(), useRef()];

  // Login screen is always dark, regardless of the in-app theme; restore on leave.
  useEffect(() => {
    const prev = document.documentElement.getAttribute("data-theme");
    document.documentElement.setAttribute("data-theme", "dark");
    document.documentElement.style.colorScheme = "dark";
    return () => {
      if (prev) {
        document.documentElement.setAttribute("data-theme", prev);
        document.documentElement.style.colorScheme = prev;
      }
    };
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  function reset() {
    setStep("phone"); setPhone(""); setOtp(["", "", "", ""]); setPendingToken(""); setError(null);
    setObName(""); setObWing(""); setObFloor(""); setObFlat(""); setStaffCode(""); setStaffPwd("");
  }

  async function sendOtp(e) {
    e?.preventDefault();
    if (phone.length !== 10) return;
    setBusy(true); setError(null);
    try {
      await api.post("/api/auth/otp/send", { phone });
      setStep("otp"); setCooldown(45);
      setTimeout(() => otpRefs[0].current?.focus(), 60);
    } catch {
      setError("Couldn't send OTP. Please try again.");
    } finally { setBusy(false); }
  }

  async function verifyOtp(e) {
    e?.preventDefault();
    const code = otp.join("");
    if (code.length !== 4) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/api/auth/otp/verify", { phone, code });
      if (data.needsOnboarding) { setPendingToken(data.pendingToken); setStep("onboard"); }
      else { applySession(data); navigate("/", { replace: true }); }
    } catch {
      setError("Invalid or expired OTP. Try again.");
    } finally { setBusy(false); }
  }

  async function onboard(e) {
    e?.preventDefault();
    if (!obName.trim() || !obWing || !obFloor || !obFlat) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/api/auth/onboard", {
        pendingToken, name: obName.trim(), wing: obWing, flatNumber: Number(obFlat),
      });
      applySession(data); navigate("/", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(msg === "flat_already_registered"
        ? "This flat is already registered. Contact the office to recover access."
        : "Could not complete sign-up. Check your flat details.");
    } finally { setBusy(false); }
  }

  async function staffLogin(e) {
    e?.preventDefault();
    if (!staffCode || !staffPwd) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/api/auth/login", { loginCode: staffCode, password: staffPwd });
      applySession(data); navigate("/", { replace: true });
    } catch {
      setError("Incorrect credentials. Please try again.");
    } finally { setBusy(false); }
  }

  function onOtpChange(i, v) {
    const ch = v.replace(/\D/g, "").slice(-1);
    const next = [...otp]; next[i] = ch; setOtp(next);
    if (ch && i < 3) otpRefs[i + 1].current?.focus();
  }
  function onOtpKey(i, e) {
    if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs[i - 1].current?.focus();
  }
  function onOtpPaste(e) {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (!text) return; e.preventDefault();
    const next = ["", "", "", ""]; for (let i = 0; i < text.length; i++) next[i] = text[i];
    setOtp(next); otpRefs[Math.min(text.length, 3)].current?.focus();
  }

  const selectedWing = WINGS.find((w) => w.code === obWing);
  const floors = selectedWing ? Array.from({ length: selectedWing.floors }, (_, i) => i + 1) : [];
  const flats = selectedWing && obFloor
    ? Array.from({ length: selectedWing.flatsPerFloor }, (_, i) => Number(obFloor) * 100 + (i + 1))
    : [];

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-aurora font-sans lg:grid lg:grid-cols-[1.1fr_minmax(430px,0.9fr)]" data-testid="login-screen">
      {/* ───────── LEFT · immersive brand panel (web only) ───────── */}
      <div ref={heroRef} onMouseMove={onHeroMove}
        className="relative hidden overflow-hidden border-r border-white/10 lg:flex lg:flex-col lg:justify-between lg:p-14 xl:p-20">
        {/* pointer-follow glow */}
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(600px circle at var(--mx,50%) var(--my,25%), rgba(0,229,255,0.12), transparent 45%)" }} />
        {/* ambient orbs */}
        <motion.div className="pointer-events-none absolute -left-24 top-4 h-80 w-80 rounded-full bg-neon-cyan/15 blur-3xl"
          animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.8, 0.5] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="pointer-events-none absolute -right-16 bottom-0 h-80 w-80 rounded-full bg-neon-lime/12 blur-3xl"
          animate={{ scale: [1.1, 1, 1.1], opacity: [0.4, 0.7, 0.4] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />

        {/* logo */}
        <div className="relative flex items-center gap-3">
          <div className="relative">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-neon-cyan text-black shadow-glow-cyan"><Buildings size={24} weight="duotone" /></div>
            <div className="absolute -right-1 -top-1 h-3 w-3 animate-pulse-soft rounded-full bg-neon-lime shadow-glow-lime" />
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Society Plus</p>
            <p className="font-display text-base font-semibold text-white">Manage Your Society, Smarter</p>
          </div>
        </div>

        {/* headline + rotating word + flow */}
        <div className="relative max-w-lg">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.24em] text-neon-cyan">
            <Sparkle size={13} weight="fill" /> One app for the whole society
          </p>
          <h1 className="font-display text-5xl font-black leading-[1.05] tracking-tight text-white xl:text-6xl">
            Your society,<br />
            <span className="relative inline-flex h-[1.1em] overflow-hidden align-bottom">
              <AnimatePresence mode="wait">
                <motion.span key={wordIdx} className="bg-gradient-to-r from-neon-cyan to-neon-lime bg-clip-text text-transparent"
                  initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -24, opacity: 0 }} transition={{ duration: 0.32 }}>
                  {HERO_WORDS[wordIdx]}
                </motion.span>
              </AnimatePresence>
            </span>
          </h1>
          <p className="mt-4 max-w-md text-[15px] leading-relaxed text-white/60">
            Visitors, complaints, amenities, staff, finance and notices — for owners, tenants, security and the committee, all in one place.
          </p>
          <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.22em] text-white/45">Every request, tracked end to end</p>
            <TaskFlowAnimation />
          </div>
        </div>

        {/* feature chips + stats */}
        <div className="relative">
          <div className="flex flex-wrap gap-2">
            {HERO_FEATURES.map((f) => (
              <span key={f.label} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[12px] font-semibold text-white/70">
                <f.icon size={14} weight="bold" className="text-neon-cyan" /> {f.label}
              </span>
            ))}
          </div>
          <div className="mt-7 flex items-center gap-8">
            {[{ n: "200", l: "Flats · 2 wings" }, { n: "16", l: "Modules" }, { n: "Live", l: "Real-time updates" }].map((s) => (
              <div key={s.l}>
                <p className="font-display text-2xl font-black text-white">{s.n}</p>
                <p className="text-[11px] uppercase tracking-wider text-white/45">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ───────── RIGHT · login card ───────── */}
      <div className="relative flex min-h-screen items-center justify-center px-5 py-10 sm:px-8">
        <div className="w-full max-w-md">
          {/* mobile brand mark (left panel is hidden below lg) */}
          <div className="mb-6 flex items-center gap-3 lg:hidden">
            <div className="relative">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-neon-cyan text-black shadow-glow-cyan"><Buildings size={24} weight="duotone" /></div>
              <div className="absolute -right-1 -top-1 h-3 w-3 animate-pulse-soft rounded-full bg-neon-lime shadow-glow-lime" />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Society Plus</p>
              <p className="font-display text-base font-semibold text-white">Manage Your Society, Smarter</p>
            </div>
          </div>

          <div className="card relative overflow-hidden border-white/10 p-6 shadow-[0_0_70px_-20px_rgba(0,229,255,0.45)] sm:p-8">
            <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-neon-cyan/15 blur-3xl" />
            <div className="relative">
              <h2 className="font-display text-2xl font-extrabold text-white">Welcome back</h2>
              <p className="mt-0.5 text-[13px] text-white/55">Sign in to your society account</p>
            </div>

            {/* Tabs */}
            <div data-testid="login-tabs" className="relative mt-5 flex w-full gap-1.5 rounded-2xl border border-white/15 bg-ink-800 p-1.5">
              {[
                { k: "resident", label: "Resident" },
                { k: "society", label: "Society Staff" },
              ].map((t) => (
                <button
                  key={t.k}
                  type="button"
                  data-testid={`login-tab-${t.k}`}
                  onClick={() => { setTab(t.k); reset(); }}
                  className={`relative flex-1 rounded-xl px-2 py-2.5 text-sm font-extrabold tracking-wide transition-all ${
                    tab === t.k
                      ? "bg-neon-lime text-black shadow-glow-lime"
                      : "bg-white/10 text-white hover:bg-white/15"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Form */}
            <div className="relative mt-5 flex min-h-[300px] flex-col">
              <AnimatePresence mode="wait">
          {tab === "resident" && step === "phone" && (
            <motion.form key="phone" onSubmit={sendOtp}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22 }} className="flex flex-1 flex-col" data-testid="phone-form">
              <div>
                <label className="label">Mobile number</label>
                <div className="flex items-stretch overflow-hidden rounded-2xl border border-white/15 bg-white/[0.03] transition-all focus-within:border-neon-cyan focus-within:ring-2 focus-within:ring-neon-cyan/30">
                  <span className="flex items-center gap-1.5 border-r border-white/10 px-3.5 text-sm font-bold text-white/80">
                    <Phone size={16} weight="bold" /> +91
                  </span>
                  <input
                    data-testid="phone-input"
                    className="flex-1 bg-transparent px-4 py-3.5 text-base text-white outline-none placeholder:text-white/30 [appearance:textfield]"
                    placeholder="10-digit mobile number" type="tel" inputMode="numeric" maxLength={10}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    required autoFocus
                  />
                </div>
                <p className="mt-2 flex items-center gap-1.5 text-[11px] text-white/45">
                  <ShieldCheck size={13} weight="fill" className="text-neon-lime" /> We'll send you a one-time code to confirm.
                </p>
              </div>

              {/* fills the empty space with ambient motion (mobile: no side panel) */}
              <div className="flex min-h-0 flex-1 items-center justify-center py-4 lg:hidden"><LoginShowcase /></div>

              <div className="mt-auto space-y-3">
                {error && <p data-testid="phone-error" className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{error}</p>}
                <button data-testid="phone-submit" className="btn-primary w-full" disabled={phone.length !== 10 || busy}>
                  {busy ? "Sending…" : <>Continue <ArrowRight weight="bold" className="ml-1" /></>}
                </button>
              </div>
            </motion.form>
          )}

          {tab === "resident" && step === "otp" && (
            <motion.form key="otp" onSubmit={verifyOtp}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22 }} className="flex flex-1 flex-col gap-5" data-testid="otp-form">
              <button type="button" onClick={() => { setStep("phone"); setError(null); }}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white/60 hover:text-white" data-testid="otp-back">
                <ArrowLeft size={14} weight="bold" /> Change number
              </button>

              <div>
                <p className="text-[12px] uppercase tracking-[0.22em] text-white/50">Enter the 4-digit code</p>
                <p className="mt-1 text-sm text-white/70">Sent to <span className="font-mono font-bold text-white">+91 {phone}</span></p>
              </div>

              <div className="flex gap-3" onPaste={onOtpPaste}>
                {otp.map((d, i) => (
                  <input
                    key={i}
                    ref={otpRefs[i]}
                    data-testid={`otp-input-${i + 1}`}
                    className="otp-cell"
                    inputMode="numeric"
                    maxLength={1}
                    autoComplete="one-time-code"
                    value={d}
                    onChange={(e) => onOtpChange(i, e.target.value)}
                    onKeyDown={(e) => onOtpKey(i, e)}
                  />
                ))}
              </div>

              <div className="mt-auto space-y-4">
                {error && <p data-testid="otp-error" className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{error}</p>}

                <button data-testid="otp-submit" className="btn-primary w-full" disabled={otp.join("").length !== 4 || busy}>
                  {busy ? "Verifying…" : <>Verify & continue <ArrowRight weight="bold" className="ml-1" /></>}
                </button>

                <button type="button" disabled={cooldown > 0}
                  onClick={() => sendOtp()}
                  className="w-full text-center text-[12px] font-semibold text-white/55 underline-offset-4 hover:underline disabled:opacity-50"
                  data-testid="otp-resend">
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
                </button>
              </div>
            </motion.form>
          )}

          {tab === "resident" && step === "onboard" && (
            <motion.form key="onboard" onSubmit={onboard}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22 }} className="flex flex-1 flex-col gap-4" data-testid="onboard-form">
              <div>
                <p className="text-[12px] uppercase tracking-[0.22em] text-white/50">Almost there</p>
                <h2 className="mt-1 font-display text-2xl font-bold text-white">Tell us about you</h2>
              </div>

              <div>
                <label className="label">Your name</label>
                <input className="field" data-testid="onboard-name" placeholder="e.g. Arun Sharma"
                  value={obName} onChange={(e) => setObName(e.target.value)} minLength={2} required autoFocus />
              </div>

              <div>
                <label className="label">Select your flat</label>
                <div className="grid grid-cols-3 gap-2">
                  <select className="field !py-3" data-testid="onboard-wing" value={obWing}
                    onChange={(e) => { setObWing(e.target.value); setObFloor(""); setObFlat(""); }}>
                    <option value="">Wing</option>
                    {WINGS.map((w) => <option key={w.code} value={w.code} className="bg-ink-800">{w.name}</option>)}
                  </select>
                  <select className="field !py-3" data-testid="onboard-floor" value={obFloor} disabled={!selectedWing}
                    onChange={(e) => { setObFloor(e.target.value); setObFlat(""); }}>
                    <option value="">Floor</option>
                    {floors.map((f) => <option key={f} value={f} className="bg-ink-800">{f}</option>)}
                  </select>
                  <select className="field !py-3" data-testid="onboard-flat" value={obFlat} disabled={!obFloor}
                    onChange={(e) => setObFlat(e.target.value)}>
                    <option value="">Flat</option>
                    {flats.map((n) => <option key={n} value={n} className="bg-ink-800">{n}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-auto space-y-4">
                {error && <p data-testid="onboard-error" className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{error}</p>}

                <button className="btn-primary w-full" data-testid="onboard-submit"
                  disabled={!obName.trim() || !obWing || !obFloor || !obFlat || busy}>
                  {busy ? "Setting up…" : <>Get started <ArrowRight weight="bold" className="ml-1" /></>}
                </button>
              </div>
            </motion.form>
          )}

          {tab === "society" && (
            <motion.form key="society" onSubmit={staffLogin}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22 }} className="flex flex-1 flex-col gap-3" data-testid="staff-form">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/50">Society Staff</p>
                <h2 className="mt-0.5 font-display text-lg font-bold text-white">Choose your role</h2>
              </div>

              <div className="space-y-1">
                {STAFF_ROLES.map((r) => {
                  const active = staffCode === r.code;
                  return (
                    <button key={r.code} type="button" data-testid={`staff-role-${r.code.toLowerCase()}`}
                      onClick={() => { setStaffCode(r.code); setError(null); }}
                      className={`flex w-full items-center gap-2 rounded-xl border px-2.5 py-1.5 text-left transition-all ${
                        active
                          ? "border-neon-cyan/60 bg-neon-cyan/[0.08] shadow-glow-cyan"
                          : "border-white/10 bg-white/[0.03] hover:border-white/20"
                      }`}>
                      <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[11px] font-extrabold ${
                        active ? "bg-neon-cyan text-black" : "bg-white/10 text-white/80"
                      }`}>{r.label[0]}</div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-bold leading-tight text-white">{r.label}</div>
                        <div className="text-[9px] leading-tight text-white/55">{r.sub}</div>
                      </div>
                      {active && <span className="chip border-neon-cyan/40 text-neon-cyan">selected</span>}
                    </button>
                  );
                })}
              </div>

              {!staffCode && (
                <div className="flex min-h-0 flex-1 items-center justify-center py-2 lg:hidden"><LoginShowcase /></div>
              )}

              {staffCode && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-auto space-y-3">
                  <div>
                    <label className="label">Password</label>
                    <div className="flex items-stretch overflow-hidden rounded-2xl border border-white/15 bg-white/[0.03] focus-within:border-neon-cyan focus-within:ring-2 focus-within:ring-neon-cyan/30">
                      <span className="flex items-center border-r border-white/10 px-3.5 text-white/60">
                        <LockKey size={16} weight="bold" />
                      </span>
                      <input data-testid="staff-password" className="flex-1 bg-transparent px-4 py-3 text-white outline-none"
                        type="password" autoComplete="current-password" placeholder="Enter password"
                        value={staffPwd} onChange={(e) => setStaffPwd(e.target.value)} required />
                    </div>
                  </div>

                  {error && <p data-testid="staff-error" className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{error}</p>}

                  <button data-testid="staff-submit" className="btn-primary w-full" disabled={!staffPwd || busy}>
                    {busy ? "Signing in…" : <>Sign in <ArrowRight weight="bold" className="ml-1" /></>}
                  </button>
                </motion.div>
              )}
            </motion.form>
          )}
              </AnimatePresence>
            </div>
          </div>

          <p className="mt-5 text-center text-[11px] text-white/35">
            By continuing you agree to our community rules · v0.1 · 2026
          </p>
        </div>
      </div>
    </div>
  );
}
